import React, {ReactElement, useEffect, useState } from 'react';
import {  
  AccountOwner, FetchReturn, Organization as OrgType, OrganizationLicense, OrganizationSubmission, SubscriptionDetails,
  User
} from '../../../../store';
import FormControl from '@mui/material/FormControl';

import { Box, styled, Typography } from '@mui/material';
import { CabButton, CabTextInput, CabImageUpload } from '@CabComponents';
import colors from '../../../../colors';
import { BILLING_INTERVAL, TIER } from '../../../../constants';
import UpgradeOverlay from '../../../../components/Common/UpgradeOverlay/UpgradeOverlay';

const StyledBox = styled(Box, {label: "StyledBox"})(() => ({
  width: '100%',
  height: '100%',
  border: '1px solid',
  borderColor: colors.black200,
  borderRadius: '4px',
}));

const TitleText = styled("div", {label: "TitleText"})({
  padding: "12px",
  fontSize: "14px",
  fontWeight: "bold",
});

interface Props {
  org: OrganizationSubmission;
  updateOrganization: (org: OrganizationSubmission) => Promise<FetchReturn<OrgType> | undefined>;
  processOrganizationLogo: (org: OrganizationSubmission) => Promise<FetchReturn<{data: string}>>;
  accountOwner: AccountOwner | undefined;
  user: User | undefined | null;
  subscriptionDetails: SubscriptionDetails | null;
  confirmButtonDisabled: boolean;
  dialogError: string;
  dialogMessage: string;
  redirectDialogOpen: boolean;
  clearPaymentDialogError: () =>  void;
  resendInvite: (license: OrganizationLicense) => void;
  onChangeSubscription: (
    newTier: TIER | null, interval: BILLING_INTERVAL | null, quantity: number | null, 
    newPromoCode: string | null, isPreview: boolean, prorationDate: string | null
  ) => Promise<SubscriptionDetails | undefined>;
}

export const Organization = ({
  org, updateOrganization, processOrganizationLogo, user, accountOwner
}: Props): ReactElement => {
  const [formChange, setFormChange] = useState(false);
  const [localOrg, setLocalOrg] = useState<OrganizationSubmission>({
    id: org.id,
    name: org.name,
    logo_file: org.logo_file,
    logo: org.logo,
    remove_background: false,
    resample_type: "nearest",
  });
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    setLocalOrg({
      id: org.id,
      name: org.name,
      logo_file: org.logo_file,
      logo: org.logo,
      remove_background: false,
      resample_type: "nearest",
    });
  }, [org]);

  const handleUpdateOrganization = async (orgUpdate: OrganizationSubmission) => {
    const res = await updateOrganization(orgUpdate);
    if (res && res.status === 200) {
      setLocalOrg({...localOrg, ...res.data});
    }
  };

  const handleOrgMetaSubmit = (e: React.FormEvent) => {
    setFormChange(false);
    e.preventDefault();
    const submitOrg = {...localOrg};
    if (file) {
      submitOrg["logo_file"] = file;
    }
    handleUpdateOrganization(submitOrg);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFormChange(false);
    e.preventDefault();
    setLocalOrg({...org, remove_background: false, resample_type: "nearest"});
  };

  const logoEditable = (
    user?.features?.WHITELABEL_LOGO  && 
    user?.permissions.CONTENT_EDITOR
  );
  const adminContact = accountOwner
    ? `${accountOwner.first_name} ${accountOwner.last_name}, ${accountOwner.email}`
    : undefined;

  return (
    <OrganizationContent>
      <StyledBox>
        <FormControl fullWidth={true}>
          <form onSubmit={handleOrgMetaSubmit} onChange={() => setFormChange(true)}>
            <Box sx={{ borderBottom: '1px solid #E2DDDD' }}>
              <Box>
                <TitleText>Organization Name</TitleText>
                { user?.permissions.CONTENT_EDITOR ?
                  <Box sx={{ paddingX: 2 }}>
                    <CabTextInput
                      placeholder='Organization Name'
                      type={'text'}
                      value={localOrg.name}
                      onChange={(e) => setLocalOrg({...localOrg, name: e.target.value})}
                      required
                      fullWidth
                      sx={{ marginTop: "4px", marginBottom: "8px" }}
                    />
                  </Box>
                  :
                  <Typography
                    sx={{
                      marginBottom: 2,
                      marginLeft: 2
                    }}>{localOrg.name}</Typography>
                }
              </Box>
            </Box>
              
            <Box>
              <TitleText>
                { user?.permissions.CONTENT_EDITOR && user?.features.WHITELABEL_LOGO ?
                  "Upload Organization Logo"
                  :
                  "Organization Logo"
                }
              </TitleText>
              <Box sx={{ paddingX: 2 }}>
                <CabImageUpload
                  currentImage={localOrg.logo}
                  currentImageName={`${localOrg.name} logo`}
                  displayMode="logo"
                  editable={!!user?.permissions.CONTENT_EDITOR}
                  onImageSelect={async (logoFile) => {
                    setFile(logoFile);
                    const res = await processOrganizationLogo({...localOrg, logo_file: logoFile});
                    if (logoFile && res.status === 200) {
                      setLocalOrg({...localOrg, logo:`data:${logoFile.type};base64,${res.data["data"]}`});
                      setFormChange(true);
                    }
                  }}
                  dragDropText="Drag & drop file or"
                  clickText="click"
                  fileTypeText="JPG or PNG. Max"
                  maxSizeText="2.5Mb"
                  sx={{ marginTop: 1 }}
                />
                {
                  user?.permissions.CONTENT_EDITOR && !user?.features?.WHITELABEL_LOGO && (
                    <UpgradeOverlay
                      show={!logoEditable}
                      overlaySx={{
                        top: 100, height: '260px', position: 'absolute', width: '100%', 
                        backgroundColor: 'rgba(0, 0, 0, .8)', left: 0, justifyContent: 'center', display: 'flex',
                      }}
                      message={'Upgrade your subscription plan to add your logo to booking pages'} />
                  )
                }
              </Box>
            </Box>
              
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingLeft: 2,
              paddingRight: 2,
              paddingTop: '10px',
            }}>
              {user?.permissions.CONTENT_EDITOR ? (
                formChange && (
                  <>
                    <CabButton
                      buttonType='secondary' 
                      onClick={handleCancel}
                      sx={{marginBottom: 2}}
                    >
                      Cancel
                    </CabButton>
                    <CabButton
                      type='submit'
                      sx={{marginBottom: 2}}
                    >
                      Save Organization
                    </CabButton>
                  </>
                )
              ) : (
                adminContact
                  ? <Box
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: 1,
                      marginTop: 1
                    }}>
                    {`To change organization information, contact your account administrator (${adminContact}).`}
                  </Box>
                  : <></>
              )}
            </Box>
          </form>
        </FormControl>
      </StyledBox>
    </OrganizationContent>
  );
};

export default Organization;

const OrganizationContent = styled(Box, {label: "OrganizationContent"})({
  width: "100%",
  maxWidth: 900,
  paddingBottom: 16,
});
